import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import DynamicTable from "../../../../../unicomponents/Tables/DynamicTable";
import BillingRecordView from "../input/BillingRecordView";
import styles from "../../css/BillingManagement.module.css";
import useStudentInfo from "../../../../../hooks/useStudentInfo";

export default function BillingListPage({
  selectedSessionId,
  schoolId,
  currentSession,
  currentSessionItems,
  sessions,
  sessionItems,
  settingData,
  resolvedStudentList,
  billingRecords,
  getBillingRecord,
  updateBillingRecord,
  updateBillingRecordsBulk,
  handleToastCenterTop,
  schoolYear,
  setPaymentMethodOverrides,
  canCreate,
  canUpdate,
  canRead,
  canAccess,
}) {
  const { t } = useTranslation();
  const { updateStudentInfo } = useStudentInfo({ schoolid: schoolId });
  const [selectedItem, setSelectedItem] = useState(null);
  const [savedHintByUser, setSavedHintByUser] = useState({});
  const saveHintTimers = useRef({});

  useEffect(() => {
    setSelectedItem(null);
  }, [selectedSessionId]);

  useEffect(() => {
    if (!selectedItem) return;
    const stillExists = currentSessionItems.some(
      (si) => si.id === selectedItem.id,
    );
    if (!stillExists) setSelectedItem(null);
  }, [currentSessionItems, selectedItem]);

  const summaryRows = useMemo(() => {
    return resolvedStudentList.map((student) => {
      const row = { ...student };
      let total = 0;
      currentSessionItems.forEach((si) => {
        const record = getBillingRecord(
          selectedSessionId,
          student.username,
          si.id,
        );
        const amount = record?.amount ?? null;
        row[`item_${si.id}`] = amount;
        total += Number(amount) || 0;
      });
      row.total = total;
      row.payment_method = student.payment_method || "";
      return row;
    });
  }, [
    resolvedStudentList,
    selectedSessionId,
    currentSessionItems,
    getBillingRecord,
  ]);

  const handlePaymentMethodChange = useCallback(
    (row, value) => {
      if (!canUpdate) {
        handleToastCenterTop(
          t("billingManagement.messages.noPaymentMethodPermission") ||
            "납부방법을 변경할 권한이 없습니다.",
        );
        return;
      }
      if (!row.studentinfo_id) {
        handleToastCenterTop(
          t("billingManagement.messages.noStudentInfoId") ||
            "학생 정보 ID가 없어 저장할 수 없습니다.",
        );
        return;
      }
      updateStudentInfo.mutate({
        id: row.studentinfo_id,
        ex_six: value || null,
      });
      setPaymentMethodOverrides?.((prev) => ({
        ...prev,
        [row.username]: value || "",
      }));
      setSavedHintByUser((prev) => ({ ...prev, [row.username]: true }));
      if (saveHintTimers.current[row.username]) {
        clearTimeout(saveHintTimers.current[row.username]);
      }
      saveHintTimers.current[row.username] = setTimeout(() => {
        setSavedHintByUser((prev) => ({ ...prev, [row.username]: false }));
      }, 1200);
    },
    [
      handleToastCenterTop,
      updateStudentInfo,
      setPaymentMethodOverrides,
      canUpdate,
      t,
    ],
  );

  const formatAmount = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    return Number(val).toLocaleString();
  };

  const tableConfig = useMemo(() => {
    const headers = [
      {
        headerKey: "hierarchyOne",
        label: t("common.labels.schoolStage"),
        style: { width: "58px" },
        sortField: "hierarchyOne",
      },
      {
        headerKey: "fullname",
        label: t("common.user.student") || "학생",
        style: { width: "78px" },
        sortField: "fullname",
      },
      {
        headerKey: "hierarchyTwo",
        label: t("common.labels.studentYear") || "학년",
        style: { width: "52px" },
        sortField: "hierarchyTwo",
      },
      {
        headerKey: "hierarchyThree",
        label: t("common.labels.class") || "반",
        style: { width: "52px" },
        sortField: "hierarchyThree",
      },
      {
        headerKey: "total",
        label: t("billingManagement.labels.total"),
        style: { width: "75px" },
        sortField: "total",
      },
      {
        headerKey: "payment_method",
        label: t("billingManagement.labels.paymentMethod") || "납부방법",
        style: { width: "82px" },
        sortField: "payment_method",
      },
      ...currentSessionItems.map((si) => ({
        headerKey: `item_${si.id}`,
        label: si.item_name,
        style: {
          width: "75px",
          minWidth: "60px",
          maxWidth: "88px",
          padding: "0 4px",
        },
        sortField: `item_${si.id}`,
      })),
    ];

    const columns = [
      { valueKey: "hierarchyOne" },
      { valueKey: "fullname" },
      { valueKey: "hierarchyTwo" },
      { valueKey: "hierarchyThree" },
      {
        valueKey: "total",
        valueFormatter: (val) => formatAmount(val),
        cellStyle: {
          textAlign: "right",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        },
      },
      {
        valueKey: "payment_method",
        valueFormatter: (val, item) => (
          <div>
            <select
              className="form-select form-select-sm p-1"
              style={{ fontSize: "12px" }}
              value={item.payment_method}
              disabled={!canUpdate}
              onChange={(e) => handlePaymentMethodChange(item, e.target.value)}
            >
              <option value="">
                {t("billingManagement.labels.selectPlaceholder") || "선택"}
              </option>
              <option value="CMS">
                {t("billingManagement.labels.paymentCMS") || "CMS"}
              </option>
              <option value="미등록">
                {t("billingManagement.labels.paymentUnregistered") || "미등록"}
              </option>
            </select>
            {savedHintByUser[item.username] && (
              <div style={{ fontSize: "11px", color: "#198754", marginTop: 2 }}>
                {t("billingManagement.listPage.paymentMethodSaved") ||
                  "저장완료"}
              </div>
            )}
          </div>
        ),
      },
      ...currentSessionItems.map((si) => ({
        valueKey: `item_${si.id}`,
        valueFormatter: (val) => formatAmount(val),
        cellStyle: {
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          width: "75px",
          padding: "0 4px",
          minWidth: "60px",
          maxWidth: "88px",
        },
      })),
    ];

    return {
      style: { fontSize: "13px", tableLayout: "auto" },
      headers,
      columns,
      rows: summaryRows,
    };
  }, [
    summaryRows,
    currentSessionItems,
    handlePaymentMethodChange,
    savedHintByUser,
    canUpdate,
    t,
  ]);

  if (!selectedSessionId) {
    return (
      <div className="alert alert-info mt-3">
        {t("billingManagement.listPage.selectPeriodFirst") ||
          "기간을 선택해주세요."}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.itemChipGrid}>
        {currentSessionItems.map((si) => {
          const isActive = selectedItem?.id === si.id;
          return (
            <button
              key={si.id}
              className={`btn btn-sm ${isActive ? "btn-primary" : "btn-outline-secondary"} ${styles.itemChip}`}
              onClick={() =>
                setSelectedItem((prev) => (prev?.id === si.id ? null : si))
              }
            >
              <span className={styles.itemChipName}>{si.item_name}</span>
              <span className={styles.itemChipAmount}>
                {si.default_amount != null
                  ? Number(si.default_amount).toLocaleString()
                  : "0"}
                원
              </span>
            </button>
          );
        })}
      </div>

      {!selectedItem && (
        <DynamicTable
          tableConfig={tableConfig}
          dataStructure="flat"
          tableStyle="cleanModern"
          sortable={true}
          initialSortField="hierarchyOne"
          initialSortDirection="asc"
          searchable={true}
          searchPlaceholder="학생 검색..."
          searchFields={["fullname", "hierarchyOne"]}
          exportable={true}
          exportFileName={`고지서_${schoolYear || ""}_${currentSession?.session_name || ""}`}
          exportButtonLabel="Excel"
          pagination={summaryRows.length > 50}
          pageSize={50}
          rowNumber={true}
        />
      )}

      {selectedItem && (
        <div className="mt-3">
          <BillingRecordView
            selectedItem={selectedItem}
            selectedSessionId={selectedSessionId}
            sessions={sessions}
            resolvedStudentList={resolvedStudentList}
            billingRecords={billingRecords}
            getBillingRecord={getBillingRecord}
            updateBillingRecord={updateBillingRecord}
            updateBillingRecordsBulk={updateBillingRecordsBulk}
            sessionItems={sessionItems}
            settingData={settingData}
            handleToastCenterTop={handleToastCenterTop}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canRead={canRead}
            canAccess={canAccess}
          />
        </div>
      )}
    </div>
  );
}
